import { Typography } from '@mui/material';
import { AcUnit } from '@mui/icons-material';

interface LoadingSteps {
  connecting: boolean;
  stupaData: boolean;
  images: boolean;
  complete: boolean;
}

interface DashboardLoadingScreenProps {
  loadingMessage?: string;
  loadingSteps?: LoadingSteps;
}

const defaultSteps: LoadingSteps = {
  connecting: true,
  stupaData: false,
  images: false,
  complete: false,
};

const DashboardLoadingScreen = ({
  loadingMessage = 'Preparing your dashboard...',
  loadingSteps = defaultSteps,
}: DashboardLoadingScreenProps) => (
  <div
    className="nilus-dashboard-loading-shell"
    role="status"
    aria-live="polite"
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      position: 'relative',
      boxSizing: 'border-box',
    }}
  >
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        animation: 'backgroundPulse 4s ease-in-out infinite',
      }}
    />

    <div
      className="nilus-dashboard-loading-card"
      style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '20px',
        padding: '60px 80px',
        boxShadow:
          '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(148, 163, 184, 0.1)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        textAlign: 'center',
        maxWidth: '600px',
        width: '100%',
        position: 'relative',
        zIndex: 2,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: '40px' }}>
        <div
          style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4)',
            animation: 'logoSpin 3s ease-in-out infinite',
          }}
        >
          <AcUnit style={{ fontSize: '40px', color: 'white' }} />
        </div>
        <Typography
          variant="h4"
          style={{
            color: '#f8fafc',
            fontWeight: 700,
            marginBottom: '12px',
            letterSpacing: '-0.025em',
          }}
        >
          Glacier Monitoring System
        </Typography>
        <Typography
          style={{
            color: '#94a3b8',
            fontSize: '1.1rem',
            fontWeight: 500,
          }}
        >
          Initializing secure connection to your glacier(s)
        </Typography>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <div
          style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '12px',
            padding: '4px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              height: '8px',
              background: 'linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)',
              borderRadius: '8px',
              width: loadingSteps.complete
                ? '100%'
                : loadingSteps.images
                  ? '75%'
                  : loadingSteps.stupaData
                    ? '50%'
                    : loadingSteps.connecting
                      ? '25%'
                      : '10%',
              transition: 'width 0.8s ease-out',
              boxShadow: '0 0 15px rgba(14, 165, 233, 0.5)',
            }}
          />
        </div>

        <Typography
          style={{
            color: '#e2e8f0',
            fontSize: '1.1rem',
            fontWeight: 600,
            marginBottom: '8px',
          }}
        >
          {loadingMessage}
        </Typography>
        <Typography style={{ color: '#64748b', fontSize: '0.9rem' }}>
          {loadingSteps.connecting && 'Establishing encrypted connection...'}
          {loadingSteps.stupaData && 'Retrieving sensor data and analytics...'}
          {loadingSteps.images && 'Loading camera feed and recent images...'}
          {loadingSteps.complete && 'Ready! Redirecting to dashboard...'}
        </Typography>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}
      >
        {[
          { key: 'connecting', icon: '🔐', label: 'Connect' },
          { key: 'stupaData', icon: '📊', label: 'Data' },
          { key: 'images', icon: '📷', label: 'Images' },
          { key: 'complete', icon: '✅', label: 'Ready' },
        ].map((step, index) => {
          const isActive = loadingSteps[step.key as keyof LoadingSteps];
          const isCompleted =
            index <
            (loadingSteps.complete
              ? 4
              : loadingSteps.images
                ? 3
                : loadingSteps.stupaData
                  ? 2
                  : loadingSteps.connecting
                    ? 1
                    : 0);

          return (
            <div
              key={step.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: isCompleted || isActive ? 1 : 0.4,
                transition: 'all 0.5s ease',
              }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: isCompleted
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : isActive
                      ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                      : 'rgba(71, 85, 105, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  marginBottom: '8px',
                  border: isActive
                    ? '2px solid rgba(14, 165, 233, 0.5)'
                    : 'none',
                  boxShadow: isActive
                    ? '0 0 20px rgba(14, 165, 233, 0.4)'
                    : 'none',
                  animation: isActive
                    ? 'dashboardLoadingStepPulse 2s ease-in-out infinite'
                    : 'none',
                }}
              >
                {step.icon}
              </div>
              <Typography
                style={{
                  fontSize: '0.8rem',
                  color: isCompleted || isActive ? '#e2e8f0' : '#64748b',
                  fontWeight: 600,
                }}
              >
                {step.label}
              </Typography>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(14, 165, 233, 0.3)',
            borderTop: '3px solid #0ea5e9',
            borderRadius: '50%',
            animation: 'dashboardLoadingSpin 1s linear infinite',
          }}
        />
        <Typography
          style={{
            color: '#94a3b8',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          Please wait while we prepare your dashboard...
        </Typography>
      </div>
    </div>

    <style>
      {`
        @keyframes dashboardLoadingSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes logoSpin {
          0%, 100% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(10deg) scale(1.05); }
        }
        @keyframes dashboardLoadingStepPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgba(14, 165, 233, 0.4); }
          50% { transform: scale(1.1); box-shadow: 0 0 30px rgba(14, 165, 233, 0.6); }
        }
        @keyframes backgroundPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        @media (max-width: 700px) {
          .nilus-dashboard-loading-card {
            padding: 40px 24px !important;
          }
        }
      `}
    </style>
  </div>
);

export default DashboardLoadingScreen;
